/**
 * Máquina de estados da PROPOSTA — função pura, sem React e sem I/O.
 *
 * Hoje o ciclo de vida da proposta existe só implicitamente, espalhado numa
 * cadeia de 7 guards que decide o botão da linha (LicitacoesTab) e em 3 cópias
 * divergentes do guard de "pode enviar". O usuário nunca vê em que ponto do
 * processo a proposta está — só um monte de botões de mesmo peso.
 *
 * Aqui isso vira explícito e único:
 *   Montagem → Documentos → Envio → Disputa → Resultado
 *
 * `deriveProposalStage` diz ONDE a proposta está; `derivePrimaryAction` diz
 * QUAL é a única ação laranja daquele estágio (guia §3b); `canSubmitProposal`
 * é o guard ÚNICO de envio (substitui as 3 versões divergentes).
 *
 * Convenções do domínio respeitadas aqui:
 *  - status do ITEM manda no fim do ciclo (proposta enviada continua existindo
 *    no histórico depois que o item foi homologado/encerrado);
 *  - item sem preço = não participa (é como o front zera lote pra "não
 *    participar"), então proposta com TODOS os itens zerados não está pronta;
 *  - checklist de declarações NUNCA bloqueia envio — só informa pendência.
 */

export type ProposalStage = 'montagem' | 'documentos' | 'envio' | 'disputa' | 'resultado';

export const STAGE_ORDER: ProposalStage[] = ['montagem', 'documentos', 'envio', 'disputa', 'resultado'];

export const STAGE_LABELS: Record<ProposalStage, string> = {
  montagem: 'Montagem',
  documentos: 'Documentos',
  envio: 'Envio',
  disputa: 'Disputa',
  resultado: 'Resultado',
};

/** Status TERMINAIS do item — o portal já decidiu (espelha TERMINAL_ITEM_STATUSES do LicitacoesTab). */
export const TERMINAL_ITEM_STATUSES = new Set([
  'desistida', 'revogada', 'descartado', 'homologado', 'encerrada', 'fracassada',
  // deprecated, ainda presentes em dados antigos:
  'ganho', 'perdido', 'adjudicado', 'anulada',
]);

/** Status TERMINAIS da proposta — não há mais o que fazer no Hub. */
export const TERMINAL_PROPOSAL_STATUSES = new Set(['aceita', 'rejeitada', 'cancelada', 'desistida']);

/** Status da proposta que significam "já foi ao portal". */
export const SENT_PROPOSAL_STATUSES = new Set(['enviada', 'aguardando_portal']);

export interface StageInput {
  proposal: any;
  item?: any | null;
  /** último job de automação da proposta (queued|running|completed|failed|needs_manual) */
  latestJob?: any | null;
  /** resumo do checklist F3 (GET declaracoes-checklist) */
  checklistSummary?: { total: number; pendentesObrigatorias: number; geradas: number; assinadas: number } | null;
  /** documentos já gerados (procurement_generated_documents) */
  generatedDocs?: any[] | null;
  /** agora (injetável pra teste determinístico) */
  now?: number;
}

export interface StageState {
  current: ProposalStage;
  done: ProposalStage[];
  /** decoração — NÃO muda o estágio; sinaliza impedimento externo */
  blocked: { reason: string; detail?: string } | null;
  /** resumo curto por estágio, pra barra de progresso */
  summary: Partial<Record<ProposalStage, string>>;
}

// ---------------------------------------------------------------- helpers

const num = (v: any): number => {
  const n = parseFloat(String(v ?? ''));
  return isNaN(n) ? 0 : n;
};

export function isJobRunning(job: any): boolean {
  return !!job && (job.status === 'queued' || job.status === 'running');
}

export function isJobBroken(job: any): boolean {
  return !!job && (job.status === 'failed' || job.status === 'needs_manual');
}

/**
 * Portal impedindo inclusão (suspensão/revogação/anulação ou evento pendente).
 * Mesma regra do `isSituacaoBlockingEvent` do LicitacoesTab — replicada aqui
 * como função pura pra máquina de estados não depender do componente.
 */
export function isPortalBlocking(situacaoCompra: any): boolean {
  if (!situacaoCompra) return false;
  if (situacaoCompra.eventoPendente === true) return true;
  if (situacaoCompra.permitirInclusao !== false) return false;
  const msg = situacaoCompra.ultimoAviso?.mensagem || '';
  return /suspen|revogad|anulad/i.test(msg);
}

/**
 * O portal ADIOU a sessão e NÃO deu data nova.
 *
 * Caso que motivou a regra (MPRJ PE 233/2026, aviso de 31/08/2026 16:26): "A
 * abertura da sessão pública desta compra foi prorrogada e ainda não há prazo
 * definido para a nova abertura". A pregoeira escreveu isso no quadro
 * informativo e não registrou o evento no sistema — para a API do ComprasNet a
 * compra seguia `PD`, `possuiAvisoDeEvento=false` e encerrando 01/09 14:00. O
 * PNCP idem. Nada de estruturado mudou, então o item ia cair na aba
 * "Encerradas" no dia seguinte e sumir da tela onde as licitações do dia são
 * controladas, esperando calado uma data que ninguém ia ver chegar.
 *
 * ⛔ NÃO derivar disto um estado GRAVADO (eventoPendente/permitirInclusao/
 * status suspensa). O aviso de suspensão fica no quadro para sempre como
 * histórico, e foi exatamente isso que criou o "falso suspenso" eterno do TST
 * 90006 (10/06/2026), que travava o envio depois da reabertura. Por isso aqui é
 * função PURA e derivada a cada render.
 *
 * A auto-limpeza está na ÚLTIMA condição, e ela olha o histórico de prazo, não
 * o prazo em si: o aviso de adiamento é publicado ANTES do prazo velho vencer
 * (é para isso que serve), então "prazo posterior ao aviso" descreve o caso
 * normal e não serviria de sinal. O que separa "adiado e parado" de "adiado e
 * já remarcado" é ter havido uma REPROGRAMAÇÃO depois do aviso, e isso o
 * `dataEncerramentoHistory` registra com carimbo de hora. Quando o scrape
 * gravar a data nova, entra uma linha ali e o selo se apaga sozinho, sem cron
 * e sem flag para alguém esquecer de baixar.
 */
function tsPortalBrt(v: any): number {
  // Os timestamps do quadro informativo vêm SEM offset (ex.
  // "2026-08-31T16:26:12.858246") e são BRT. Sem ancorar o -03:00 o valor
  // mudaria de significado conforme o fuso do navegador.
  const str = String(v ?? '');
  if (!str) return NaN;
  const comFuso = /Z$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str) ? str : `${str}-03:00`;
  return new Date(comFuso).getTime();
}

/**
 * Status em que a ABERTURA DA SESSÃO ainda está por acontecer. O selo só faz
 * sentido aqui: é sobre uma sessão que foi adiada e não tem data nova.
 *
 * Medido na base de produção (119 itens com proposta ativa, 01/09/2026): sem
 * este recorte a regra acendia em 3 itens, e 2 eram falso positivo — Paulo
 * Afonso 26/2026 ("Suspenderemos o certame e retornaremos amanhã (07/08) às
 * 8h") e TRT-11 76/2026 ("Esta sessão será suspensa e reaberta dia 1/9/26, às
 * 9h"). Nos dois a suspensão foi NO MEIO da sessão, com a data de volta escrita
 * em texto livre que o parser de `previsaoReabertura` não pega (ele só entende
 * "publicação prevista para DD/MM/AAAA"), e nos dois a disputa já tinha
 * acontecido: o item estava em `aguardando_julgamento`. O selo ficaria aceso
 * para sempre num item que já seguiu adiante.
 */
const STATUS_PRE_SESSAO = new Set(['novo', 'analisando', 'interessado', 'participando', 'suspensa']);

export function isProrrogadoSemData(item: any, now = Date.now()): boolean {
  if (!item) return false;
  // Cobre de uma vez os terminais e o `aguardando_julgamento` — quem já passou
  // da sessão não tem sessão adiada.
  if (!STATUS_PRE_SESSAO.has(String(item.status || ''))) return false;

  const aviso = item?.situacaoCompra?.ultimoAviso;
  if (!aviso) return false;
  // Com data prevista de reabertura não há o que sinalizar: o próprio
  // applySituacaoCompraToItem já joga a previsão em dataSessaoInicio.
  if (aviso.previsaoReabertura) return false;
  if (!/prorrog|adiad|suspens/i.test(String(aviso.mensagem || ''))) return false;

  const avisoTs = tsPortalBrt(aviso.dataHoraInclusao);
  if (isNaN(avisoTs)) return false;

  // Sessão já remarcada para o futuro: tem data nova, só que não no prazo de proposta. Caso do CREA-PB 17/2026 (item
  // 15162, 21/10/2026): o aviso de 16/10 prorrogou a ABERTURA DA SESSÃO para 27/10, o scrape gravou a data nova em
  // dataSessaoInicio, e o dataEncerramento (prazo de proposta, 15/10) já tinha passado e não muda mais. Olhando só o
  // dataEncerramento e o histórico dele, a regra nunca via a remarcação e o selo "adiada sem data nova" ficava falso.
  const sessao = item?.dataSessaoInicio ? new Date(item.dataSessaoInicio).getTime() : NaN;
  if (!isNaN(sessao) && sessao > now) return false;

  const enc = item?.dataEncerramento ? new Date(item.dataEncerramento).getTime() : NaN;
  if (isNaN(enc)) return false;
  // Prazo corrente ainda no futuro: o item já se sustenta em Ativas sozinho.
  if (enc > now) return false;

  const hist: any[] = Array.isArray(item.dataEncerramentoHistory) ? item.dataEncerramentoHistory : [];
  const remarcadoDepoisDoAviso = hist.some((h) => {
    const t = new Date(String(h?.changedAt ?? '')).getTime();
    return !isNaN(t) && t > avisoTs;
  });
  return !remarcadoDepoisDoAviso;
}

export function isDeadlinePassed(item: any, now = Date.now()): boolean {
  const d = item?.dataEncerramento;
  if (!d) return false;
  const ts = new Date(d).getTime();
  return !isNaN(ts) && ts < now;
}

/**
 * Itens com preço > 0 = os que a IPCOM realmente está ofertando.
 *
 * Item julgado por MAIOR DESCONTO é a exceção: o que o operador oferta é o
 * PERCENTUAL, e ele pode ser 0 (ofertar o próprio valor estimado) ou 100
 * (unitário zerado). Nos dois casos há oferta e o `unitPrice` derivado
 * mentiria — em 0% dizendo que há preço mesmo sem ninguém ter preenchido, e em
 * 100% dizendo que não há. Quem responde é o campo estar preenchido.
 */
export function pricedItems(proposal: any): any[] {
  const items: any[] = Array.isArray(proposal?.proposalItems) ? proposal.proposalItems : [];
  return items.filter((it) =>
    it?.priceMode === 'desconto'
      ? it.discountPercent != null && it.discountPercent !== ''
      : num(it?.unitPrice) > 0,
  );
}

/**
 * Próximo evento relevante da licitação: a data FUTURA mais próxima entre o fim
 * do envio (dataEncerramento) e a sessão (dataSessaoInicio/Fim). Se tudo já
 * passou, devolve o evento mais RECENTE (para ordenar e para encerrar).
 *
 * Resolve "a proposta encerrou mas a disputa é daqui a 5 dias" (IFES): o item
 * continua ativo e ordena pela data da SESSÃO, não pelo fim da proposta.
 */
export function proposalNextEventTs(
  item: any,
  now = Date.now(),
): { ts: number; future: boolean; kind: 'prop' | 'sessao' } | null {
  if (!item) return null;
  const events: { ts: number; kind: 'prop' | 'sessao' }[] = [];
  if (item.dataEncerramento) events.push({ ts: new Date(item.dataEncerramento).getTime(), kind: 'prop' });
  if (item.dataSessaoInicio) events.push({ ts: new Date(item.dataSessaoInicio).getTime(), kind: 'sessao' });
  if (item.dataSessaoFim) events.push({ ts: new Date(item.dataSessaoFim).getTime(), kind: 'sessao' });
  const valid = events.filter((e) => Number.isFinite(e.ts));
  if (!valid.length) return null;
  const future = valid.filter((e) => e.ts > now).sort((a, b) => a.ts - b.ts);
  if (future.length) return { ts: future[0].ts, future: true, kind: future[0].kind };
  const latest = [...valid].sort((a, b) => b.ts - a.ts)[0];
  return { ts: latest.ts, future: false, kind: latest.kind };
}

export interface ProposalBuckets {
  ativas: any[];
  cotacoes: any[];
  encerradas: any[];
  arquivadas: any[];
  todas: any[];
}

/**
 * COTAÇÃO / PRÉ-EDITAL: o órgão mandou o TR e pediu orçamento, mas ainda não
 * publicou o edital. Não há número de compra nem data nenhuma para acompanhar.
 *
 * Existe porque os baldes só sabiam encerrar por DATA: proposta sem
 * dataEncerramento e sem sessão nunca vira "Encerrada" e nunca é terminal, então
 * ficava em Ativas para sempre (PGDF/STFC, item 11306, cadastrado em 14/08/2026
 * pelo modo "sem edital publicado" do Importar licitação). É o caminho normal de
 * um orçamento pedido por TR, não uma exceção.
 *
 * A saída é automática e não depende de ninguém lembrar de desarquivar: assim
 * que o portal trouxer uma data OU o número do pregão for preenchido, a regra
 * deixa de valer e a proposta volta sozinha para Ativas.
 *
 * Não colide com isProrrogadoSemData: aquilo exige `dataEncerramento` válida
 * (prazo vencido esperando remarcação), e aqui não há data nenhuma.
 */
export function isCotacaoPreEdital(proposal: any, now = Date.now()): boolean {
  const item = proposal?.item;
  if (!item) return false;
  // Qualquer data agendada — envio ou sessão, futura ou passada — já dá o que
  // acompanhar, e a classificação por prazo volta a fazer sentido.
  if (proposalNextEventTs(item, now)) return false;
  return !String(item.numeroCompra ?? '').trim();
}

/**
 * Classifica CADA proposta em exatamente um balde — a base única das abas, dos
 * stat cards e da lista.
 *
 * Existe porque a mesma regra vivia em TRÊS cópias no LicitacoesTab, duas delas
 * com um comentário afirmando estar sincronizadas com a terceira. Não estavam
 * no que importa: os stat cards contavam `!archived` (tudo que não foi
 * arquivado) enquanto a lista filtrava `!archived && !encerrada && !terminal`.
 * O card dizia "Precisam reenviar 3", o clique filtrava e vinham 2 — a
 * terceira era a proposta 274 (SÃO PAULO SSP), item homologado e prazo vencido
 * em 07/08/2026. "Propostas ativas 107" ao lado da aba "Ativas (5)" era a mesma
 * doença.
 *
 * Um balde por proposta, por construção: contador e lista não têm COMO divergir.
 */
export function classifyProposals(proposals: any[], now = Date.now()): ProposalBuckets {
  // R1.13: "encerrada" = passou pra ontem ou antes. Hoje continua ativa mesmo
  // se a hora já passou — o usuário precisa ver para acompanhar.
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
  const isEncerrada = (p: any) => {
    // Adiado sem data nova NÃO é encerrado: o prazo velho venceu, mas a
    // licitação continua viva esperando remarcação. Deixar cair em "Encerradas"
    // tira da tela do dia a única licitação que ainda pode voltar a qualquer
    // momento (MPRJ PE 233/2026). O selo se apaga sozinho quando a data nova
    // chega — ver isProrrogadoSemData.
    if (isProrrogadoSemData(p?.item, now)) return false;
    const ne = proposalNextEventTs(p?.item, now);
    return ne ? ne.ts < startOfToday.getTime() : false;
  };
  // F2 (24/05/2026): item em status TERMINAL sai de Ativas/Encerradas e vai
  // para Arquivadas. O critério é o status do ITEM, não o da proposta.
  //
  // "Desistida" com prazo ABERTO não é arquivo: desistir é o desfazer que a
  // própria tela manda usar para corrigir proposta já enviada, e arquivá-la faz
  // a licitação sumir de quem acabou de desistir para reenviar (Jumirim BLL
  // 12/2026, em 18/08/2026, com 14 dias de prazo).
  const isItemTerminal = (p: any) =>
    TERMINAL_ITEM_STATUSES.has(p?.item?.status)
    && !(p?.item?.status === 'desistida' && !isDeadlinePassed(p?.item, now));

  const ativas: any[] = [], cotacoes: any[] = [], encerradas: any[] = [], arquivadas: any[] = [];
  for (const p of proposals || []) {
    // Arquivar/descartar continua vencendo tudo: o que o usuário tirou da frente
    // não volta pela porta das cotações.
    if (p?.archived || isItemTerminal(p)) arquivadas.push(p);
    else if (isCotacaoPreEdital(p, now)) cotacoes.push(p);
    else if (isEncerrada(p)) encerradas.push(p);
    else ativas.push(p);
  }
  return { ativas, cotacoes, encerradas, arquivadas, todas: [...(proposals || [])] };
}

// ---------------------------------------------------------------- estágio

export function deriveProposalStage(input: StageInput): StageState {
  const { proposal, item, latestJob, checklistSummary, generatedDocs } = input;
  const now = input.now ?? Date.now();

  const items: any[] = Array.isArray(proposal?.proposalItems) ? proposal.proposalItems : [];
  const priced = pricedItems(proposal);
  const hasItems = items.length > 0;
  const hasPricedItems = priced.length > 0;
  const docsCount = Array.isArray(generatedDocs)
    ? generatedDocs.filter((d) => d?.status !== 'cancelado').length
    : 0;
  const pendentes = checklistSummary?.pendentesObrigatorias ?? 0;

  const summary: Partial<Record<ProposalStage, string>> = {};
  if (hasItems) {
    summary.montagem = `${priced.length}/${items.length} item(ns) precificado(s)`;
  }
  // A barra responde "o que me falta", não "o que eu tenho" (decisão 11/08).
  if (pendentes > 0) {
    summary.documentos = `${pendentes} exigência(s) pendente(s)`;
  } else if (docsCount > 0) {
    summary.documentos = checklistSummary?.assinadas
      ? `${docsCount} documento(s) · ${checklistSummary.assinadas} assinado(s)`
      : `${docsCount} documento(s) gerado(s)`;
  } else {
    summary.documentos = 'nenhum documento gerado';
  }
  if (proposal?.submittedVia) {
    summary.envio = proposal.submittedVia.startsWith('robo')
      ? 'enviada pelo robô'
      : proposal.submittedVia === 'manual_flip' ? 'marcada manualmente' : 'somente monitoramento';
  }
  if (proposal?.disputeSessionId) summary.disputa = 'sessão criada';

  // blocked é DECORAÇÃO — nunca muda o estágio.
  let blocked: StageState['blocked'] = null;
  if (isPortalBlocking(item?.situacaoCompra)) {
    blocked = {
      reason: 'Portal bloqueando inclusão',
      detail: item?.situacaoCompra?.ultimoAviso?.mensagem || 'evento pendente de publicação',
    };
  } else if (isJobBroken(latestJob)) {
    blocked = {
      reason: latestJob.status === 'needs_manual' ? 'Automação exige ação manual' : 'Última automação falhou',
      detail: latestJob.errorMessage || undefined,
    };
  }

  const stageAt = (current: ProposalStage): StageState => ({
    current,
    done: STAGE_ORDER.slice(0, STAGE_ORDER.indexOf(current)),
    blocked,
    summary,
  });

  // 1. RESULTADO — terminal (proposta ou item já decididos)
  if (TERMINAL_PROPOSAL_STATUSES.has(proposal?.status) || TERMINAL_ITEM_STATUSES.has(item?.status)) {
    return stageAt('resultado');
  }

  // 2. DISPUTA — já foi ao portal (inclui aguardando_julgamento do item)
  if (SENT_PROPOSAL_STATUSES.has(proposal?.status) || item?.status === 'aguardando_julgamento') {
    return stageAt('disputa');
  }

  // 3/4/5. Ainda em preparação: prazo vencido é impedimento, não estágio.
  if (isDeadlinePassed(item, now) && !blocked) {
    blocked = { reason: 'Prazo de envio encerrado', detail: 'a licitação já encerrou o recebimento de propostas' };
  }

  // 3. ENVIO — itens precificados e sem pendência obrigatória de documento
  if (hasPricedItems && pendentes === 0) return stageAt('envio');

  // 4. DOCUMENTOS — itens prontos, documentação pendente
  if (hasPricedItems) return stageAt('documentos');

  // 5. MONTAGEM — default (sem itens ou tudo zerado)
  return stageAt('montagem');
}

// ---------------------------------------------------------------- envio

export interface SubmitCheck {
  ok: boolean;
  /** motivos de impedimento, prontos pra listar na tela */
  reasons: string[];
}

/**
 * Guard ÚNICO de envio ao portal. Substitui as 3 versões divergentes de hoje
 * (dropdown ⋮ exigia noActiveJob && !isEncerrada; o drawer só exigia 1 item
 * precificado; a cadeia do primaryBtn tinha ainda outra combinação).
 */
export interface DocsCheck {
  /** Documentos obrigatórios do processo ainda sem arquivo. */
  faltando: string[];
  /** Atendidos por PDF gerado que o portal só aceita assinado. */
  naoAssinados: string[];
  /** Portal pede documento mas o HUB não sabe quais (PCP). */
  listaIndisponivel?: boolean;
}

export function canSubmitProposal(input: {
  proposal: any;
  item?: any | null;
  latestJob?: any | null;
  /** Documentos do processo. `null`/ausente = portal sem upload (ComprasNet). */
  docsCheck?: DocsCheck | null;
  now?: number;
}): SubmitCheck {
  const { proposal, item, latestJob, docsCheck } = input;
  const now = input.now ?? Date.now();
  const reasons: string[] = [];

  if (!item?.id) reasons.push('Proposta não está vinculada a uma licitação');
  if (pricedItems(proposal).length === 0) {
    reasons.push('Nenhum item com preço unitário preenchido');
  }
  if (isJobRunning(latestJob)) reasons.push('Já existe uma automação em andamento');
  if (isDeadlinePassed(item, now)) reasons.push('Prazo de envio já encerrado');
  if (isPortalBlocking(item?.situacaoCompra)) {
    const msg = item?.situacaoCompra?.ultimoAviso?.mensagem;
    reasons.push(`Portal bloqueando inclusão${msg ? ` — ${String(msg).slice(0, 80)}` : ''}`);
  }
  // "Desistida" NÃO é fim de linha para o envio.
  //
  // Desistir é o DESFAZER que a própria tela manda usar quando a proposta já
  // foi ao portal ("Pra corrigir, use Desistir e reenvie"). Contá-la como
  // terminal criava um beco sem saída: o operador desistia para corrigir e
  // descobria que não conseguia mais cadastrar — com a licitação aberta.
  // (Jumirim BLL 12/2026 em 18/08/2026, ainda com 14 dias de prazo.)
  //
  // Quem barra desistência fora de hora é o `isDeadlinePassed` acima, que já
  // roda: prazo encerrado continua bloqueando, como deve.
  const naoTerminal = (s?: string) => s === 'desistida';
  if (TERMINAL_PROPOSAL_STATUSES.has(proposal?.status) && !naoTerminal(proposal?.status)) {
    reasons.push(`Proposta em status terminal (${proposal.status})`);
  }
  if (TERMINAL_ITEM_STATUSES.has(item?.status) && !naoTerminal(item?.status)) {
    reasons.push(`Licitação em status terminal (${item.status})`);
  }

  // Documento obrigatório do processo é impedimento REAL: o envio BLL/BNC já
  // barrava por isso, só que em background, depois do clique. A tela dizia
  // "✓ Tudo certo para enviar" com 1 obrigatório sem arquivo logo abaixo.
  if (docsCheck?.faltando?.length) {
    const lista = docsCheck.faltando.slice(0, 2).map((d) => `"${d}"`).join(', ');
    reasons.push(
      `${docsCheck.faltando.length} documento(s) obrigatório(s) do processo sem arquivo: ${lista}` +
        (docsCheck.faltando.length > 2 ? '…' : ''),
    );
  }
  // Gerado e não assinado NÃO bloqueia por si — bloqueia porque o slot fica sem
  // arquivo, e isso já entrou acima. Aqui só se explica o porquê.
  if (docsCheck?.naoAssinados?.length) {
    reasons.push(
      `${docsCheck.naoAssinados.length} documento(s) gerado(s) mas não assinado(s) — o portal exige assinado`,
    );
  }

  return { ok: reasons.length === 0, reasons };
}

// ---------------------------------------------------------------- ação primária

export type PrimaryActionId =
  | 'carregar-itens'
  | 'sincronizar'
  | 'salvar'
  | 'ir-documentos'
  | 'gerar-documentos'
  | 'enviar'
  | 'reenviar'
  | 'marcar-enviada'
  | 'gravar-itens'
  | 'acompanhar-envio'
  | 'criar-disputa'
  | 'abrir-disputa'
  | 'adequar'
  | 'marcar-resultado';

export interface PrimaryAction {
  id: PrimaryActionId;
  label: string;
  disabled: boolean;
  /** por que está desabilitada (tooltip) */
  reason?: string;
}

/**
 * A ÚNICA ação laranja da tela, derivada do estágio que o usuário está vendo
 * (viewStage) — não do estágio "real". Se ele clicou em Documentos, a ação
 * primária é a de Documentos.
 */
export function derivePrimaryAction(
  viewStage: ProposalStage,
  state: StageState,
  ctx: {
    proposal: any;
    item?: any | null;
    latestJob?: any | null;
    dirty?: boolean;
    hasItems?: boolean;
    convocacaoAdequacao?: boolean;
    /** Documentos do processo — o botão precisa da MESMA resposta da seção Envio. */
    docsCheck?: DocsCheck | null;
    now?: number;
    /**
     * O portal do item tem envio pelo HUB (`envioPeloHub`, a etapa `cadastrar` da matriz)? 'nao' troca o "Enviar
     * proposta" pelo "Já enviei manualmente". Ausente ou 'desconhecido' = como sempre.
     */
    envioPeloHub?: 'sim' | 'nao' | 'desconhecido' | 'grava_itens';
    /**
     * No modo `grava_itens` (o HUB grava os itens no portal e para): o estado da gravação, para a ação do topo ser a
     * MESMA da etapa Envio (ver useGravacaoNoPortal).
     */
    /**
     * `exigeConfirmacao`: a proposta ainda não existe no portal, e gravar é CADASTRAR e ENVIAR (decisão de 19/10 21:05).
     * `enviada`: o PORTAL aceitou o envio do HUB (a lista ainda pode não ter relido o status).
     */
    gravacao?: { podeGravar: boolean; gravando: boolean; gravada: boolean; comRecusado: boolean; exigeConfirmacao?: boolean; enviada?: boolean; semNovoEnvio?: boolean } | null;
  },
): PrimaryAction {
  const { proposal, item, latestJob, dirty } = ctx;
  const hasItems = ctx.hasItems ?? (Array.isArray(proposal?.proposalItems) && proposal.proposalItems.length > 0);
  const running = isJobRunning(latestJob);

  if (dirty) {
    return { id: 'salvar', label: 'Salvar alterações', disabled: false };
  }

  switch (viewStage) {
    case 'montagem': {
      if (!hasItems) {
        return {
          id: 'carregar-itens',
          label: 'Carregar itens do portal',
          disabled: running,
          reason: running ? 'Automação em andamento' : undefined,
        };
      }
      // Itens carregados mas NENHUM precificado: o passo seguinte é buscar os
      // valores no portal, não "ir para Documentos" — mandar seguir adiante
      // com a proposta zerada é empurrar o usuário pro caminho errado.
      if (pricedItems(proposal).length === 0) {
        return {
          id: 'sincronizar',
          label: 'Sincronizar do portal',
          disabled: running,
          reason: running ? 'Automação em andamento' : 'Traz do portal os valores estimados e os dados dos itens',
        };
      }
      return { id: 'ir-documentos', label: 'Ir para Documentos', disabled: false };
    }

    case 'documentos':
      return {
        id: 'gerar-documentos',
        label: 'Gerar documentos',
        disabled: !hasItems,
        reason: !hasItems ? 'Monte os itens da proposta primeiro' : undefined,
      };

    case 'envio': {
      if (running) return { id: 'acompanhar-envio', label: 'Acompanhar envio', disabled: false };
      const jaEnviada = SENT_PROPOSAL_STATUSES.has(proposal?.status);
      // Portal sem envio pelo HUB (Licitanet, Licitar Digital…): oferecer "Enviar" leva à recusa do backend (SAAE
      // Ituiutaba, 18/10/2026). O caminho é o portal + "Já enviei manualmente"; enviada, o passo seguinte é a disputa.
      // Portal que GRAVA ITENS e para (Licitanet): gravar, e depois a confirmação é do Heitor no portal ("Já enviei").
      if (ctx.envioPeloHub === 'grava_itens' && !jaEnviada) {
        const g = ctx.gravacao;
        if (g?.gravando) {
          return g.exigeConfirmacao
            ? { id: 'gravar-itens', label: 'Enviando ao portal…', disabled: true, reason: 'Envio em andamento' }
            : { id: 'gravar-itens', label: 'Gravando no portal…', disabled: true, reason: 'Gravação em andamento' };
        }
        // O portal aceitou o envio do HUB: o backend marcou a proposta como enviada, e a lista relê em seguida.
        if (g?.enviada) return { id: 'gravar-itens', label: 'Enviada ao portal', disabled: true, reason: 'A proposta foi enviada pelo HUB' };
        // ⛔ O portal recusou o envio (ou não se sabe se chegou): nada de mandar de novo; o caminho é a tela do portal.
        if (g?.semNovoEnvio) return { id: 'marcar-enviada', label: 'Já enviei', disabled: false };
        // Proposta que ainda não existe no portal: o botão é o ENVIO, com confirmação.
        if (g?.exigeConfirmacao) {
          return {
            id: 'gravar-itens',
            label: 'Enviar proposta ao portal',
            disabled: !g.podeGravar,
            reason: g.podeGravar ? undefined : 'Complete o que falta na prévia, na etapa Envio',
          };
        }
        if (g?.gravada && !g.comRecusado) return { id: 'marcar-enviada', label: 'Já enviei', disabled: false };
        return {
          id: 'gravar-itens',
          label: g?.gravada ? 'Gravar de novo' : 'Gravar itens no portal',
          disabled: !g?.podeGravar,
          reason: g?.podeGravar ? undefined : 'Complete o que falta na prévia, na etapa Envio',
        };
      }
      // Sem envio pelo HUB, ou o modo que só grava (já enviada pelo Heitor no portal): não existe "Reenviar ao portal".
      if (ctx.envioPeloHub === 'nao' || ctx.envioPeloHub === 'grava_itens') {
        if (!jaEnviada) return { id: 'marcar-enviada', label: 'Já enviei manualmente', disabled: false };
        return proposal?.disputeSessionId
          ? { id: 'abrir-disputa', label: 'Abrir disputa', disabled: false }
          : { id: 'criar-disputa', label: 'Acompanhar disputa', disabled: false };
      }
      const check = canSubmitProposal({ proposal, item, latestJob, docsCheck: ctx.docsCheck, now: ctx.now });
      return {
        id: jaEnviada ? 'reenviar' : 'enviar',
        label: jaEnviada ? 'Reenviar ao portal' : 'Enviar proposta',
        disabled: !check.ok,
        reason: check.ok ? undefined : check.reasons[0],
      };
    }

    case 'disputa':
      if (ctx.convocacaoAdequacao) {
        return { id: 'adequar', label: '⚡ Adequar ao lance final', disabled: false };
      }
      if (!proposal?.disputeSessionId) {
        return { id: 'criar-disputa', label: 'Acompanhar disputa', disabled: false };
      }
      return { id: 'abrir-disputa', label: 'Abrir disputa', disabled: false };

    case 'resultado':
    default:
      return {
        id: 'marcar-resultado',
        label: 'Marcar resultado',
        disabled: false,
      };
  }
}

/**
 * Tom do badge de estágio (chave de statusColors.TONE_BADGE). L5 (24/10/2026, padrão da Central): o estágio é ANDAMENTO, e
 * andamento é neutro. Antes cada estágio tinha a sua cor (âmbar, azul, azul, âmbar pulsando, verde), e o "Disputa" pulsava
 * dias antes da sessão. O que pede ação tem sinal próprio: o ⚠ do bloqueio, o selo do job que falhou e o cartão
 * "Precisam de ação" do Cadastrar.
 */
export const STAGE_TONE: Record<ProposalStage, 'neutral'> = {
  montagem: 'neutral',
  documentos: 'neutral',
  envio: 'neutral',
  disputa: 'neutral',
  resultado: 'neutral',
};
